import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Plus, Minus, Edit, Trash2, Ticket, Settings, ShoppingBag, X,
  CheckCircle2, AlertTriangle, Loader2, Tag, Calendar,
  Mail, Phone, ChevronDown, ChevronUp, Ban, Banknote, Printer,
  ShieldCheck, ShieldX, Search, LogIn
} from 'lucide-react';
import {
  getAdminTicketPage, saveTicketPageSettings,
  getAdminTicketTypes, addTicketType, updateTicketType, deleteTicketType,
  getTicketOrders, getTicketOrderDetail, cancelTicketOrder, confirmTicketPayment,
  declineTicketPayment, checkInTicketOrder, adminTicketWalkIn, getTicketTodayStats,
  updateTicketOrder, uploadTicketTypeImage, uploadTicketPageBanner,
} from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { useHotelFeatures } from '../context/HotelFeaturesContext';
import { Navigate } from 'react-router-dom';

const COLORS = [
  { label: 'Blue',   value: '#2563EB' },
  { label: 'Indigo', value: '#4F46E5' },
  { label: 'Purple', value: '#7C3AED' },
  { label: 'Green',  value: '#059669' },
  { label: 'Orange', value: '#EA580C' },
  { label: 'Red',    value: '#DC2626' },
  { label: 'Teal',   value: '#0D9488' },
  { label: 'Pink',   value: '#DB2777' },
];

const ALL_TABS = ['Settings', 'Ticket Types', 'Orders', 'Sell Tickets'];
const STAFF_TABS = ['Orders', 'Sell Tickets'];
const EMPTY_TYPE = { name: '', code: '', typeId: '0', price: '', minQuantity: '1', personsPerTicket: '1', description: '', color: '#2563EB', imageUrl: '', inclusions: [''] };

const TicketCard = ({ ticket, isActive }) => (
  <div
    className="rounded-xl overflow-hidden shadow-lg flex"
    style={{ background: isActive ? ticket.color || '#2563EB' : '#475569' }}
  >
    {/* Stub */}
    <div className="w-14 flex flex-col items-center justify-center border-r-2 border-dashed border-white/40 px-1 py-4 gap-2">
      <div className="w-5 h-5 rounded-full bg-slate-900/60 -ml-[25px]" />
      <p className="text-white/90 font-bold text-xs tracking-widest [writing-mode:vertical-rl] rotate-180 select-none">
        {ticket.code || 'CODE'}
      </p>
      <div className="w-5 h-5 rounded-full bg-slate-900/60 -ml-[25px]" />
    </div>
    {/* Main */}
    <div className="flex-1 p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-white font-bold text-base leading-tight">{ticket.name}</p>
          <p className="text-white/70 text-xs mt-0.5">{ticket.typeId === 1 ? 'Corporate' : 'Normal'} Ticket</p>
        </div>
        <p className="text-white font-bold text-lg">৳{Number(ticket.price).toLocaleString()}</p>
      </div>
      {ticket.typeId === 1 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {ticket.minQuantity > 1 && <p className="text-white/70 text-xs">Min. {ticket.minQuantity} tickets</p>}
          {ticket.personsPerTicket > 1 && <p className="text-white/80 text-xs font-semibold">· {ticket.personsPerTicket} persons/ticket</p>}
        </div>
      )}
      {ticket.inclusions?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ticket.inclusions.map((inc, i) => (
            <span key={i} className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">{inc}</span>
          ))}
        </div>
      )}
      {!isActive && <p className="text-white/60 text-xs mt-2 italic">Inactive</p>}
    </div>
  </div>
);

const TicketPage = () => {
  const { user } = useAuth();
  const isHead       = user?.role === 'HotelHead' || user?.role === 'Admin';
  const canManageTypes = isHead || user?.role === 'HotelAdmin';
  const { isTicketEnabled, featuresLoading } = useHotelFeatures();
  const { toast } = useToast();

  if (!featuresLoading && !isTicketEnabled) return <Navigate to="/dashboard" replace />;
  if (user?.role === 'HotelStaff' && !user?.canSellTickets) return <Navigate to="/dashboard" replace />;

  const isStaff = user?.role === 'HotelStaff';
  const TABS = isStaff ? STAFF_TABS : ALL_TABS;

  const [activeTab, setActiveTab] = useState('Settings');
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Settings state
  const [settingsForm, setSettingsForm] = useState({ pageTitle: '', subTitle: '', description: '', bannerImageUrl: '', isActive: true });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  // Types state
  const [types, setTypes] = useState([]);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState(EMPTY_TYPE);
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [todayStats, setTodayStats] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(null); // holds orderId being checked in

  // Verify payment dialog
  const [verifyTarget, setVerifyTarget] = useState(null); // { orderId, orderRef, totalAmount, method, txnId, submittedAmount }
  const [verifyAmount, setVerifyAmount] = useState('');
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const [declineTarget, setDeclineTarget] = useState(null); // { orderId, orderRef }
  const [declineReason, setDeclineReason] = useState('');
  const [declineLoading, setDeclineLoading] = useState(false);

  // Edit order
  const [editTarget, setEditTarget]   = useState(null); // { orderId, orderRef }
  const [editForm, setEditForm]       = useState({ leadName: '', leadPhone: '', visitDate: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Walk-in (Sell Tickets)
  const [walkInStep, setWalkInStep] = useState(1);
  const [walkInItems, setWalkInItems] = useState({});
  const [walkInCustomer, setWalkInCustomer] = useState({ name: '', phone: '', visitDate: new Date().toISOString().split('T')[0] });
  const [walkInPaymentMethod, setWalkInPaymentMethod] = useState('Cash');
  const [walkInLoading, setWalkInLoading] = useState(false);
  const [walkInSuccess, setWalkInSuccess] = useState(null);

  const loadPage = useCallback(async () => {
    try {
      const data = await getAdminTicketPage();
      setPageData(data);
      setSettingsForm({
        pageTitle: data.pageTitle || 'Resort Tickets',
        subTitle: data.subTitle || '',
        description: data.description || '',
        bannerImageUrl: data.bannerImageUrl || '',
        isActive: data.isActive ?? true,
      });
      setTypes(data.ticketTypes || []);
    } catch {
      toast({ title: 'Failed to load ticket page', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadOrders = useCallback(async (search = searchQuery) => {
    setOrdersLoading(true);
    try {
      const params = {};
      if (orderFilter === 'active')         params.cancelled = false;
      if (orderFilter === 'cancelled')      params.cancelled = true;
      if (orderFilter === 'today')          params.visitToday = true;
      if (orderFilter === 'checkedIn')      { params.visitToday = true; params.checkedIn = true; }
      if (orderFilter === 'notCheckedIn')   { params.visitToday = true; params.checkedIn = false; }
      if (orderFilter === 'noShow')         params.noShow = true;
      if (search?.trim()) params.search = search.trim();
      setOrders(await getTicketOrders(params));
    } catch {
      toast({ title: 'Failed to load orders', variant: 'destructive' });
    } finally {
      setOrdersLoading(false);
    }
  }, [toast, orderFilter, searchQuery]);

  const loadTodayStats = useCallback(async () => {
    try { setTodayStats(await getTicketTodayStats()); } catch { }
  }, []);

  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => {
    if (user?.role === 'HotelStaff') setActiveTab('Sell Tickets');
  }, [user?.role]);
  useEffect(() => {
    if (activeTab === 'Orders') { loadOrders(); loadTodayStats(); }
  }, [activeTab, loadOrders, loadTodayStats]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let bannerUrl = settingsForm.bannerImageUrl;
      if (bannerFile) {
        setBannerUploading(true);
        try {
          bannerUrl = await uploadTicketPageBanner(bannerFile);
          setBannerFile(null);
        } finally { setBannerUploading(false); }
      }
      await saveTicketPageSettings({ ...settingsForm, bannerImageUrl: bannerUrl });
      setSettingsForm(p => ({ ...p, bannerImageUrl: bannerUrl }));
      toast({ title: 'Settings saved' });
      await loadPage();
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const openAddType = () => { setEditingType(null); setTypeForm({ ...EMPTY_TYPE, inclusions: [''] }); setImageFile(null); setIsTypeDialogOpen(true); };
  const openEditType = (t) => {
    setEditingType(t);
    setTypeForm({
      name: t.name, code: t.code, typeId: String(t.typeId),
      price: String(t.price), minQuantity: String(t.minQuantity),
      personsPerTicket: String(t.personsPerTicket || 1),
      description: t.description || '', color: t.color || '#2563EB',
      imageUrl: t.imageUrl || '',
      inclusions: t.inclusions?.length ? t.inclusions : [''],
    });
    setImageFile(null);
    setIsTypeDialogOpen(true);
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: typeForm.name, code: typeForm.code.toUpperCase(),
        typeId: parseInt(typeForm.typeId), price: parseFloat(typeForm.price),
        minQuantity: parseInt(typeForm.minQuantity) || 1,
        personsPerTicket: parseInt(typeForm.personsPerTicket) || 1,
        description: typeForm.description || null, color: typeForm.color,
        imageUrl: typeForm.imageUrl || null,
        inclusions: typeForm.inclusions.filter(s => s.trim()),
      };

      let savedId = editingType?.id;
      if (editingType) {
        await updateTicketType(editingType.id, payload);
        toast({ title: 'Ticket type updated' });
      } else {
        // addTicketType doesn't return the new ID — reload and find by code
        await addTicketType(payload);
        toast({ title: 'Ticket type added' });
      }

      // Upload image if a file was selected
      if (imageFile) {
        setImageUploading(true);
        try {
          // For new types, find the newly created type by code after reload
          if (!savedId) {
            const fresh = await getAdminTicketTypes();
            const match = fresh.find(t => t.code === payload.code.toUpperCase());
            savedId = match?.id;
          }
          if (savedId) {
            const url = await uploadTicketTypeImage(savedId, imageFile);
            setTypeForm(p => ({ ...p, imageUrl: url }));
          }
        } catch (imgErr) {
          toast({ title: 'Type saved but image upload failed: ' + imgErr.message, variant: 'destructive' });
        } finally {
          setImageUploading(false);
        }
      }

      setIsTypeDialogOpen(false);
      await loadPage();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id) => {
    try {
      await deleteTicketType(id);
      toast({ title: 'Ticket type deactivated' });
      await loadPage();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const toggleOrderDetail = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); setOrderDetail(null); return; }
    setExpandedOrder(orderId);
    setDetailLoading(true);
    try {
      setOrderDetail(await getTicketOrderDetail(orderId));
    } catch { toast({ title: 'Failed to load order details', variant: 'destructive' }); }
    finally { setDetailLoading(false); }
  };

  const openEditOrder = (order) => {
    setEditTarget({ orderId: order.id, orderRef: order.orderRef });
    setEditForm({
      leadName:  order.leadName || '',
      leadPhone: order.leadPhone || '',
      visitDate: order.visitDate ? order.visitDate.slice(0, 10) : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.leadName.trim()) { toast({ title: 'Guest name is required', variant: 'destructive' }); return; }
    if (!editForm.visitDate) { toast({ title: 'Visit date is required', variant: 'destructive' }); return; }
    setEditLoading(true);
    try {
      await updateTicketOrder(editTarget.orderId, {
        leadName:  editForm.leadName.trim(),
        leadPhone: editForm.leadPhone.trim() || null,
        visitDate: editForm.visitDate,
      });
      toast({ title: 'Order updated successfully' });
      setEditTarget(null);
      await loadOrders();
      if (expandedOrder === editTarget.orderId)
        setOrderDetail(await getTicketOrderDetail(editTarget.orderId));
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setEditLoading(false); }
  };

  const handleCancelOrder = async (id) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await cancelTicketOrder(id);
      toast({ title: 'Order cancelled' });
      await loadOrders();
      setExpandedOrder(null);
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const handleConfirmPayment = async (orderId) => {
    setPaymentLoading(true);
    try {
      const updated = await confirmTicketPayment(orderId);
      setOrderDetail(updated);
      await loadOrders();
      toast({ title: 'Payment confirmed' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const openDecline = (detail, listItem) => {
    setDeclineReason('');
    setDeclineTarget({ orderId: listItem.id, orderRef: listItem.orderRef });
  };

  const handleDeclineConfirm = async () => {
    if (!declineReason.trim()) { toast({ title: 'Please enter a reason for declining', variant: 'destructive' }); return; }
    setDeclineLoading(true);
    try {
      const updated = await declineTicketPayment(declineTarget.orderId, declineReason);
      setOrderDetail(updated);
      setDeclineTarget(null);
      await loadOrders();
      toast({ title: 'Payment declined. Guest can re-submit.' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setDeclineLoading(false);
    }
  };

  const openVerify = (detail, listItem) => {
    setVerifyTarget({
      orderId:         listItem.id,
      orderRef:        listItem.orderRef,
      totalAmount:     listItem.totalAmount,
      method:          detail.manualPaymentMethod || '',
      txnId:           detail.manualPaymentRef || '',
      submittedAmount: detail.manualPaymentAmount > 0 ? detail.manualPaymentAmount : null,
    });
    setVerifyAmount(String(detail.manualPaymentAmount > 0 ? detail.manualPaymentAmount : listItem.totalAmount));
    setVerifyChecked(false);
  };

  const handleVerifyConfirm = async () => {
    setVerifyLoading(true);
    try {
      const updated = await confirmTicketPayment(verifyTarget.orderId);
      setOrderDetail(updated);
      setVerifyTarget(null);
      await loadOrders();
      toast({ title: 'Payment verified and confirmed' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setVerifyLoading(false);
    }
  };

  const isVisitDateReached = (visitDateStr) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const visit = new Date(visitDateStr + 'T00:00:00');
    return visit <= today;
  };

  const handleCheckIn = async (orderId) => {
    setCheckInLoading(orderId);
    try {
      await checkInTicketOrder(orderId);
      toast({ title: 'Guest checked in successfully' });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, isCheckedIn: true } : o));
      if (orderDetail?.id === orderId) setOrderDetail(prev => ({ ...prev, isCheckedIn: true }));
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setCheckInLoading(null);
    }
  };

  const setWalkInQty = (typeId, qty) => {
    setWalkInItems(prev => {
      const next = { ...prev };
      if (qty <= 0) { delete next[typeId]; } else { next[typeId] = qty; }
      return next;
    });
  };

  const resetWalkIn = () => {
    setWalkInStep(1);
    setWalkInItems({});
    setWalkInCustomer({ name: '', phone: '', visitDate: new Date().toISOString().split('T')[0] });
    setWalkInPaymentMethod('Cash');
    setWalkInSuccess(null);
  };

  const walkInTotal = types.filter(t => t.isActive).reduce((sum, t) => sum + (walkInItems[t.id] || 0) * t.price, 0);
  const walkInItemCount = Object.values(walkInItems).reduce((s, q) => s + q, 0);

  const handleWalkIn = async () => {
    setWalkInLoading(true);
    try {
      const items = Object.entries(walkInItems)
        .filter(([, qty]) => qty > 0)
        .map(([typeId, qty]) => ({ ticketTypeId: parseInt(typeId), quantity: qty }));
      const result = await adminTicketWalkIn({
        leadName: walkInCustomer.name.trim(),
        leadPhone: walkInCustomer.phone?.trim() || null,
        visitDate: walkInCustomer.visitDate,
        items,
        paymentMethod: walkInPaymentMethod,
      });
      setWalkInSuccess(result);
      toast({ title: 'Walk-in tickets issued successfully' });
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setWalkInLoading(false);
    }
  };

  const printThermalVoucher = (detail) => {
    if (!detail) return;
    const paperSize = localStorage.getItem('xhotel_thermal_paper_size') || '80mm';
    const pageWidth = paperSize === 'A4' ? '210mm' : paperSize;

    const fmtVisitDate = (str) => {
      if (!str) return '—';
      const parts = str.split('-');
      if (parts.length !== 3) return str;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${parts[2]} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    };
    const fmtNow = () => new Date().toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });

    const isPaid = detail.paymentStatus === 'Paid';
    const itemLines = (detail.items || []).map(i => {
      const personsNote = (i.personsPerTicket > 1) ? ` (${i.quantity * i.personsPerTicket} persons)` : '';
      return `<div class="item-name">${i.quantity}x ${i.ticketTypeName}${personsNote}</div>` +
        `<div class="item-price">৳${Number(i.unitPrice).toLocaleString()} x ${i.quantity} = ৳${Number(i.subTotal).toLocaleString()}</div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<style>
  @page { margin: 1mm 2mm; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:8.5pt; background:#fff; color:#000; }
  .wrap { max-width:${pageWidth}; margin:0 auto; padding:2mm 2mm; }
  .div  { border-top:1px solid #000; margin:4px 0; }
  .dash { border-top:1px dashed #666; margin:4px 0; }
  .center { text-align:center; }
  .kv { display:flex; justify-content:space-between; font-size:8pt; padding:1.5px 0; }
  .item-name  { font-size:8.5pt; padding-top:3px; }
  .item-price { font-size:7.5pt; text-align:right; padding-bottom:3px; }
  .total-line { display:flex; justify-content:space-between; font-weight:bold; font-size:12pt; margin:5px 0 3px; }
  .footer-note { text-align:center; font-size:7pt; line-height:1.6; margin-top:3px; }
</style>
<script>window.onload = function(){ window.focus(); window.print(); }</script>
</head><body>
<div class="wrap">

  <div class="center" style="margin-bottom:4px">
    <div style="font-weight:bold;font-size:10.5pt;letter-spacing:0.5px">${(detail.hotelName || 'RESORT').toUpperCase()}</div>
    <div style="font-size:7.5pt;margin-top:1px">Ticket Receipt</div>
  </div>

  <div class="div"></div>
  <div class="center" style="font-size:8pt;font-weight:bold;letter-spacing:1px;padding:2px 0">─── TICKET RECEIPT ───</div>
  <div class="div"></div>

  <div class="kv"><span>Ref</span><span>${detail.orderRef}</span></div>
  <div class="kv"><span>Guest</span><span>${detail.leadName}</span></div>
  ${detail.leadPhone ? `<div class="kv"><span>Phone</span><span>${detail.leadPhone}</span></div>` : ''}
  <div class="kv"><span>Visit Date</span><span>${fmtVisitDate(detail.visitDate)}</span></div>
  <div class="kv"><span>Status</span><span style="font-weight:bold">${isPaid ? 'PAID' : 'PENDING'}</span></div>

  <div class="dash"></div>
  ${itemLines}
  <div class="dash"></div>

  <div class="total-line">
    <span>TOTAL</span>
    <span>৳${Number(detail.totalAmount).toLocaleString()}</span>
  </div>

  <div class="div"></div>
  ${detail.bookedByName ? `<div class="kv"><span>Booked by</span><span>${detail.bookedByName}</span></div>` : ''}
  <div class="kv"><span>Printed</span><span>${fmtNow()}</span></div>
  <div class="dash"></div>

  <div class="footer-note">
    <div>Present at the resort entrance.</div>
    <div>Non-transferable. Valid on visit date only.</div>
  </div>

</div>
</body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const printTicketVoucher = (detail) => {
    if (!detail) return;
    const money = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Ticket Voucher — ${detail.orderRef}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#1a1a2e; }
  .page { max-width:700px; margin:0 auto; padding:32px 40px; }
  .header { display:flex; align-items:center; justify-content:space-between; padding-bottom:18px; border-bottom:3px solid #1e3a8a; margin-bottom:22px; }
  .hotel-name { font-size:20px; font-weight:700; color:#1e3a8a; }
  .hotel-sub  { font-size:12px; color:#555; margin-top:2px; }
  .voucher-title { font-size:22px; font-weight:800; color:#1e3a8a; text-transform:uppercase; letter-spacing:1px; }
  .ref-box { display:inline-block; margin-top:6px; background:#1e3a8a; color:#fff; padding:4px 14px; border-radius:4px; font-size:13px; font-weight:700; letter-spacing:2px; }
  .ribbon { display:flex; align-items:center; justify-content:center; gap:8px; padding:10px 20px; border-radius:8px; margin-bottom:20px; font-weight:700; font-size:13px; background:#dcfce7; color:#15803d; border:1.5px solid #86efac; }
  .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
  .section { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:14px; }
  .section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#64748b; margin-bottom:8px; }
  .row { display:flex; justify-content:space-between; padding:3px 0; border-bottom:1px solid #f1f5f9; font-size:13px; }
  .row:last-child { border-bottom:none; }
  .row .lbl { color:#64748b; }
  .row .val { font-weight:600; color:#0f172a; }
  .ticket-table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  .ticket-table th { background:#1e3a8a; color:#fff; padding:8px 12px; font-size:12px; text-align:left; }
  .ticket-table td { padding:8px 12px; font-size:13px; border-bottom:1px solid #f1f5f9; }
  .ticket-table tr:nth-child(even) td { background:#f8fafc; }
  .total-row td { background:#1e3a8a !important; color:#fff; font-weight:700; font-size:14px; }
  .footer { margin-top:24px; padding-top:14px; border-top:2px dashed #e2e8f0; display:flex; justify-content:space-between; align-items:flex-end; }
  .terms { font-size:11px; color:#94a3b8; max-width:55%; }
  .terms p { margin-bottom:3px; }
  .sig-line { width:150px; border-top:1px solid #334155; margin:28px auto 4px; }
  .sig-label { font-size:11px; color:#64748b; text-align:center; }
  .generated { text-align:center; margin-top:18px; font-size:11px; color:#94a3b8; }
  @media print { .no-print { display:none !important; } body { background:#fff; } .page { padding:16px 24px; max-width:100%; } }
</style>
</head>
<body>
<div class="page">

  <div style="text-align:right;margin-bottom:14px;" class="no-print">
    <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:9px 22px;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;">
      🖨️ Print Voucher
    </button>
  </div>

  <div class="header">
    <div>
      <div class="hotel-name">${detail.hotelName || 'Hotel'}</div>
      <div class="hotel-sub">Resort Ticket Voucher</div>
    </div>
    <div style="text-align:right;">
      <div class="voucher-title">Ticket Voucher</div>
      <div class="ref-box">${detail.orderRef}</div>
    </div>
  </div>

  <div class="ribbon">✅ PAYMENT CONFIRMED — ${money(detail.totalAmount)}</div>

  <div class="info-grid">
    <div class="section">
      <div class="section-title">Lead Guest</div>
      <div class="row"><span class="lbl">Name</span><span class="val">${detail.leadName}</span></div>
      ${detail.leadEmail ? `<div class="row"><span class="lbl">Email</span><span class="val">${detail.leadEmail}</span></div>` : ''}
      ${detail.leadPhone ? `<div class="row"><span class="lbl">Phone</span><span class="val">${detail.leadPhone}</span></div>` : ''}
    </div>
    <div class="section">
      <div class="section-title">Order Details</div>
      <div class="row"><span class="lbl">Order Ref</span><span class="val" style="font-family:monospace">${detail.orderRef}</span></div>
      <div class="row"><span class="lbl">Visit Date</span><span class="val">${detail.visitDate}</span></div>
      <div class="row"><span class="lbl">Booked On</span><span class="val">${detail.createdAt ? new Date(detail.createdAt.endsWith?.('Z') ? detail.createdAt : detail.createdAt+'Z').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</span></div>
      <div class="row"><span class="lbl">Payment</span><span class="val" style="color:#15803d">${detail.paymentStatus}</span></div>
    </div>
  </div>

  <table class="ticket-table">
    <thead>
      <tr>
        <th>Ticket Type</th>
        <th>Code</th>
        <th style="text-align:center">Qty</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Sub Total</th>
      </tr>
    </thead>
    <tbody>
      ${(detail.items || []).map(item => `
      <tr>
        <td>${item.ticketTypeName}${item.inclusions?.length > 0 ? `<br/><span style="font-size:11px;color:#64748b;">${item.inclusions.join(' · ')}</span>` : ''}</td>
        <td style="font-family:monospace;color:#1e3a8a;">${item.ticketTypeCode}</td>
        <td style="text-align:center;font-weight:600">${item.quantity}</td>
        <td style="text-align:right">${money(item.unitPrice)}</td>
        <td style="text-align:right;font-weight:600">${money(item.subTotal)}</td>
      </tr>`).join('')}
      <tr class="total-row">
        <td colspan="4">Total (${(detail.items || []).reduce((s,i)=>s+i.quantity,0)} tickets)</td>
        <td style="text-align:right">${money(detail.totalAmount)}</td>
      </tr>
    </tbody>
  </table>

  <div class="footer">
    <div class="terms">
      <p>• Please present this voucher at the resort entrance.</p>
      <p>• Valid only for the visit date shown above.</p>
      <p>• Tickets are non-transferable and non-refundable.</p>
      <p>• This is a computer-generated document.</p>
    </div>
    <div>
      <div class="sig-line"></div>
      <div class="sig-label">Authorised Signature</div>
    </div>
  </div>

  <div class="generated">Generated on ${new Date().toLocaleString('en-GB')} · ${detail.hotelName || ''}</div>
</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  if (loading) return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-bold text-white">Tickets</h1></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1,2,3].map(i => <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse"><CardContent className="p-6 h-32"/></Card>)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Resort Tickets</h1>
          <p className="text-slate-400 mt-1">Manage ticket types, page settings and orders</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border ${
            pageData?.isTicketEnabled ? 'bg-green-600/20 text-green-400 border-green-600/40' : 'bg-red-600/20 text-red-400 border-red-600/40'
          }`}>
            {pageData?.isTicketEnabled ? <CheckCircle2 className="h-3.5 w-3.5"/> : <AlertTriangle className="h-3.5 w-3.5"/>}
            {pageData?.isTicketEnabled ? 'Feature Enabled' : 'Disabled by TexoHotel'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-lg border border-slate-700 w-fit">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              (TABS.includes(activeTab) ? activeTab : TABS[0]) === tab ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
            }`}>
            {tab}
          </button>
        ))}
      </div>

      {/* ── Settings Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'Settings' && !isStaff && (
        <div>
          <Card className="bg-slate-800/50 border-slate-700 max-w-2xl">
            <CardContent className="p-6">
              <form onSubmit={handleSaveSettings} className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-white">Page Settings</h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm text-slate-400">Active</span>
                    <div
                      onClick={() => setSettingsForm(p => ({ ...p, isActive: !p.isActive }))}
                      className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${settingsForm.isActive ? 'bg-green-600' : 'bg-slate-600'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow ${settingsForm.isActive ? 'left-5' : 'left-0.5'}`} />
                    </div>
                  </label>
                </div>
                <div className="space-y-2">
                  <Label>Page Title</Label>
                  <Input value={settingsForm.pageTitle} onChange={e => setSettingsForm(p => ({ ...p, pageTitle: e.target.value }))}
                    className="bg-slate-700 border-slate-600" required />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input value={settingsForm.subTitle} onChange={e => setSettingsForm(p => ({ ...p, subTitle: e.target.value }))}
                    className="bg-slate-700 border-slate-600" placeholder="e.g. Experience the full resort" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={settingsForm.description} onChange={e => setSettingsForm(p => ({ ...p, description: e.target.value }))}
                    className="bg-slate-700 border-slate-600" rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Banner Image <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
                  {settingsForm.bannerImageUrl && !bannerFile && (
                    <div className="flex items-center gap-3 bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2">
                      <img src={settingsForm.bannerImageUrl} alt="banner" className="h-10 w-16 object-cover rounded" />
                      <span className="text-slate-400 text-xs flex-1 truncate">Current banner</span>
                      <button type="button" onClick={() => setSettingsForm(p => ({ ...p, bannerImageUrl: '' }))}
                        className="text-xs text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  )}
                  <input
                    type="file" accept="image/*"
                    onChange={e => setBannerFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer cursor-pointer"
                  />
                  {bannerFile && <p className="text-xs text-green-400">Ready to upload: {bannerFile.name}</p>}
                  {bannerUploading && (
                    <p className="text-xs text-blue-400 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Uploading banner…
                    </p>
                  )}
                </div>
                <Button type="submit" disabled={saving || !canManageTypes} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Settings
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Ticket Types Tab ──────────────────────────────────────────────── */}
      {activeTab === 'Ticket Types' && !isStaff && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-slate-400 text-sm">{types.length} ticket type{types.length !== 1 ? 's' : ''} defined</p>
            {canManageTypes && (
              <Button onClick={openAddType} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />Add Ticket Type
              </Button>
            )}
          </div>

          {types.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <Ticket className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Ticket Types Yet</h3>
                <p className="text-slate-400 mb-4">Create your first ticket type to start selling.</p>
                {canManageTypes && <Button onClick={openAddType} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2"/>Add First Ticket Type</Button>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {types.map(t => (
                <div key={t.id} className="space-y-3">
                  <TicketCard ticket={t} isActive={t.isActive} />
                  {canManageTypes && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditType(t)} className="flex-1 border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 h-7 text-xs">
                        <Edit className="h-3 w-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteType(t.id)} className="flex-1 border-red-600/50 text-red-400 hover:bg-red-600/20 h-7 text-xs">
                        <Trash2 className="h-3 w-3 mr-1" />{t.isActive ? 'Deactivate' : 'Deactivated'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Orders Tab ───────────────────────────────────────────────────── */}
      {activeTab === 'Orders' && (
        <div className="space-y-4">

          {/* Today stats strip */}
          {todayStats && (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setOrderFilter('today')} className={`rounded-lg p-3 text-center border transition-all ${orderFilter === 'today' ? 'bg-blue-600/30 border-blue-500' : 'bg-blue-900/20 border-blue-700/40 hover:border-blue-600/60'}`}>
                <p className="text-xl font-bold text-blue-400">{todayStats.visitingToday}</p>
                <p className="text-xs text-slate-400 mt-0.5">Visiting Today</p>
              </button>
              <button onClick={() => setOrderFilter('checkedIn')} className={`rounded-lg p-3 text-center border transition-all ${orderFilter === 'checkedIn' ? 'bg-green-600/30 border-green-500' : 'bg-green-900/20 border-green-700/40 hover:border-green-600/60'}`}>
                <p className="text-xl font-bold text-green-400">{todayStats.checkedInToday}</p>
                <p className="text-xs text-slate-400 mt-0.5">Checked In</p>
              </button>
              <button onClick={() => setOrderFilter('notCheckedIn')} className={`rounded-lg p-3 text-center border transition-all ${orderFilter === 'notCheckedIn' ? 'bg-amber-600/30 border-amber-500' : 'bg-amber-900/20 border-amber-700/40 hover:border-amber-600/60'}`}>
                <p className="text-xl font-bold text-amber-400">{todayStats.notCheckedInToday}</p>
                <p className="text-xs text-slate-400 mt-0.5">Pending Today</p>
              </button>
            </div>
          )}

          {/* Search + filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadOrders(searchQuery)}
                placeholder="Search by order ID, email or phone…"
                className="pl-8 bg-slate-800 border-slate-600 text-white h-8 text-sm"
              />
            </div>
            <Button size="sm" onClick={() => loadOrders(searchQuery)} className="bg-blue-600 hover:bg-blue-700 h-8 text-xs px-3">
              <Search className="h-3.5 w-3.5 mr-1.5" />Search
            </Button>
            <div className="flex flex-wrap gap-1 bg-slate-800/50 p-1 rounded-lg border border-slate-700">
              {[['all','All'],['active','Active'],['today','Today'],['notCheckedIn','Not Checked In'],['noShow','No-Show'],['cancelled','Cancelled']].map(([v,l]) => (
                <button key={v} onClick={() => setOrderFilter(v)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${orderFilter === v ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                  {l}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => { loadOrders(); loadTodayStats(); }} className="border-slate-600 text-slate-300 h-8 text-xs">Refresh</Button>
          </div>

          {ordersLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-blue-400 animate-spin"/></div>
          ) : orders.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <ShoppingBag className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No ticket orders found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {orders.map(o => (
                <Card key={o.id} className={`border-slate-700 ${o.isCancelled ? 'bg-slate-800/20 opacity-70' : 'bg-slate-800/50'}`}>
                  <CardContent className="p-0">
                    <button className="w-full p-4 text-left flex items-center justify-between" onClick={() => toggleOrderDetail(o.id)}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-mono font-semibold text-sm">{o.orderRef}</span>
                            {o.isCancelled && <Badge className="bg-red-900/50 text-red-400 border border-red-700/50 text-xs">Cancelled</Badge>}
                            <Badge className={`text-xs border-0 ${o.paymentStatus === 'Paid' ? 'bg-green-600 text-white' : 'bg-amber-700/60 text-amber-300'}`}>{o.paymentStatus}</Badge>
                            {o.isCheckedIn && (
                              <span className="flex items-center gap-1 text-xs text-teal-300 bg-teal-900/40 border border-teal-700/40 rounded px-1.5 py-0.5">
                                <LogIn className="h-3 w-3" /> Checked In
                              </span>
                            )}
                            {o.isNoShow && !o.isCancelled && (
                              <span className="text-xs text-red-400 bg-red-900/30 border border-red-700/40 rounded px-1.5 py-0.5">No-Show</span>
                            )}
                            {o.paymentStatus === 'Pending' && o.manualPaymentRef && !o.isCancelled && (
                              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-900/30 border border-emerald-700/40 rounded px-1.5 py-0.5">
                                <ShieldCheck className="h-3 w-3" /> Payment Submitted
                              </span>
                            )}
                          </div>
                          <p className="text-slate-300 text-sm mt-0.5">{o.leadName} · {o.ticketSummary}</p>
                          <p className="text-slate-500 text-xs mt-0.5">Visit: {o.visitDate} · Booked: {new Date(o.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        <span className="text-white font-bold">৳{Number(o.totalAmount).toLocaleString()}</span>
                        {!o.isCancelled && !o.isCheckedIn && !o.isNoShow && o.paymentStatus === 'Paid' && isVisitDateReached(o.visitDate) && (
                          <button
                            onClick={e => { e.stopPropagation(); handleCheckIn(o.id); }}
                            disabled={checkInLoading === o.id}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                          >
                            {checkInLoading === o.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <LogIn className="h-3 w-3" />}
                            IN
                          </button>
                        )}
                        {expandedOrder === o.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </div>
                    </button>

                    {expandedOrder === o.id && (
                      <div className="border-t border-slate-700 p-4 space-y-4">
                        {detailLoading ? (
                          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-400"/></div>
                        ) : orderDetail ? (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              <div className="space-y-1.5">
                                <h4 className="text-slate-400 text-xs font-medium uppercase tracking-wide">Lead Info</h4>
                                <p className="text-white font-medium">{orderDetail.leadName}</p>
                                <div className="flex items-center gap-1.5 text-slate-400"><Mail className="h-3.5 w-3.5"/>{orderDetail.leadEmail}</div>
                                {orderDetail.leadPhone && <div className="flex items-center gap-1.5 text-slate-400"><Phone className="h-3.5 w-3.5"/>{orderDetail.leadPhone}</div>}
                                <div className="flex items-center gap-1.5 text-slate-400"><Calendar className="h-3.5 w-3.5"/>Visit: {orderDetail.visitDate}</div>
                              </div>
                              <div className="space-y-1.5">
                                <h4 className="text-slate-400 text-xs font-medium uppercase tracking-wide">Ticket Items</h4>
                                {orderDetail.items.map(item => (
                                  <div key={item.id} className="flex items-center justify-between">
                                    <span className="text-slate-300">{item.quantity}× <span className="text-white font-medium">{item.ticketTypeName}</span> <span className="text-slate-500 text-xs">({item.ticketTypeCode})</span>
                                      {item.personsPerTicket > 1 && <span className="text-blue-400 text-xs ml-1">· {item.quantity * item.personsPerTicket} persons</span>}
                                    </span>
                                    <span className="text-white font-medium">৳{Number(item.subTotal).toLocaleString()}</span>
                                  </div>
                                ))}
                                <div className="border-t border-slate-700 pt-1.5 flex justify-between font-semibold">
                                  <span className="text-slate-300">Total</span>
                                  <span className="text-white">৳{Number(orderDetail.totalAmount).toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                            {/* Guest payment submission info */}
                            {orderDetail?.manualPaymentRef && orderDetail?.paymentStatus === 'Pending' && (
                              <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-3 space-y-2 text-sm">
                                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Guest Payment Submission</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-400">Method</span>
                                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    {orderDetail.manualPaymentMethod || '—'}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-400">Transaction ID</span>
                                  <span className="font-mono text-green-400 text-xs bg-green-900/30 px-2 py-0.5 rounded border border-green-700/40">
                                    {orderDetail.manualPaymentRef}
                                  </span>
                                </div>
                                {orderDetail.manualPaymentAmount > 0 && (
                                  <div className="flex items-center justify-between border-t border-emerald-700/30 pt-2">
                                    <span className="text-slate-400">Amount Sent</span>
                                    <span className="text-emerald-300 font-bold">৳{Number(orderDetail.manualPaymentAmount).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2 pt-1">
                              {/* Verify + Decline buttons when guest submitted payment */}
                              {!o.isCancelled && orderDetail?.paymentStatus === 'Pending' && orderDetail?.manualPaymentRef && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => openVerify(orderDetail, o)}
                                    disabled={paymentLoading}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                                  >
                                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5"/>
                                    Verify Payment
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => openDecline(orderDetail, o)}
                                    disabled={paymentLoading}
                                    className="bg-red-700 hover:bg-red-800 text-white h-8 text-xs"
                                  >
                                    <ShieldX className="h-3.5 w-3.5 mr-1.5"/>
                                    Decline
                                  </Button>
                                </>
                              )}
                              {/* Manual confirm when no submission */}
                              {!o.isCancelled && orderDetail?.paymentStatus === 'Pending' && !orderDetail?.manualPaymentRef && (
                                <Button
                                  size="sm"
                                  onClick={() => handleConfirmPayment(o.id)}
                                  disabled={paymentLoading}
                                  className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                                >
                                  {paymentLoading
                                    ? <Loader2 className="h-3 w-3 animate-spin mr-1"/>
                                    : <Banknote className="h-3.5 w-3.5 mr-1.5"/>}
                                  Confirm Payment
                                </Button>
                              )}
                              {orderDetail?.paymentStatus === 'Paid' && !o.isCancelled && (
                                <Button
                                    size="sm"
                                    onClick={() => printThermalVoucher(orderDetail)}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                                  >
                                    <Printer className="h-3.5 w-3.5 mr-1.5"/>
                                    Print
                                  </Button>
                              )}
                              {!o.isCancelled && !o.isCheckedIn && (
                                <Button size="sm" variant="outline" onClick={() => openEditOrder(o)}
                                  className="border-blue-600/50 text-blue-400 hover:bg-blue-600/20 h-8 text-xs">
                                  <Edit className="h-3 w-3 mr-1"/>Edit
                                </Button>
                              )}
                              {isHead && !o.isCancelled && (
                                <Button size="sm" variant="outline" onClick={() => handleCancelOrder(o.id)}
                                  className="border-red-600/50 text-red-400 hover:bg-red-600/20 h-8 text-xs">
                                  <Ban className="h-3 w-3 mr-1"/>Cancel Order
                                </Button>
                              )}
                            </div>

                            {/* ── Staff Activity Info ── */}
                            {(orderDetail?.bookedByName || orderDetail?.declinedByName) && (
                              <div className="bg-slate-700/30 border border-slate-700 rounded-xl p-3 space-y-1.5 text-sm">
                                <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-2">Staff Activity</p>
                                {orderDetail.bookedByName && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-slate-400">Booked by</span>
                                    <span className="text-white font-medium">{orderDetail.bookedByName}</span>
                                  </div>
                                )}
                                {orderDetail.declinedByName && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-slate-400">Payment declined by</span>
                                    <span className="text-red-400 font-medium">{orderDetail.declinedByName}</span>
                                  </div>
                                )}
                                {orderDetail.declineReason && (
                                  <div className="flex items-start justify-between gap-4">
                                    <span className="text-slate-400 flex-shrink-0">Decline reason</span>
                                    <span className="text-red-300 text-xs text-right">{orderDetail.declineReason}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Sell Tickets Tab ─────────────────────────────────────────────── */}
      {activeTab === 'Sell Tickets' && (
        <div className="space-y-6">
          {/* Step indicator */}
          {!walkInSuccess && (
            <div className="flex items-center gap-2">
              {['Select Tickets', 'Customer Info', 'Payment'].map((label, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center gap-2 ${walkInStep === i + 1 ? 'text-white' : walkInStep > i + 1 ? 'text-green-400' : 'text-slate-500'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      walkInStep > i + 1 ? 'bg-green-600' : walkInStep === i + 1 ? 'bg-blue-600' : 'bg-slate-700'
                    }`}>{walkInStep > i + 1 ? '✓' : i + 1}</div>
                    <span className="text-sm hidden sm:block">{label}</span>
                  </div>
                  {i < 2 && <div className="h-px bg-slate-700 w-12 flex-shrink-0"/>}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Success */}
          {walkInSuccess && (
            <Card className="bg-green-900/20 border-green-700/50 max-w-md">
              <CardContent className="p-6 space-y-4 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto"/>
                <div>
                  <p className="text-white font-bold text-lg">Tickets Issued!</p>
                  <p className="text-slate-400 text-sm mt-1">Order Ref: <span className="font-mono text-blue-400">{walkInSuccess.orderRef}</span></p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => printThermalVoucher(walkInSuccess)}>
                    <Printer className="h-4 w-4 mr-1.5"/>Print
                  </Button>
                  <Button variant="outline" className="border-slate-600" onClick={resetWalkIn}>
                    <Plus className="h-4 w-4 mr-1.5"/>New Sale
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Select Tickets */}
          {!walkInSuccess && walkInStep === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3">
                {types.filter(t => t.isActive).length === 0 && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-8 text-center text-slate-500">No active ticket types configured.</CardContent>
                  </Card>
                )}
                {types.filter(t => t.isActive).map(t => (
                  <Card key={t.id} className={`bg-slate-800/50 border-slate-700 transition-colors ${(walkInItems[t.id] || 0) > 0 ? 'border-blue-500/50' : ''}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{t.name}</p>
                        <p className="text-slate-400 text-sm">৳{Number(t.price).toLocaleString()} per ticket</p>
                        {t.personsPerTicket > 1 && <p className="text-blue-400 text-xs font-medium">Admits {t.personsPerTicket} persons/ticket</p>}
                        {t.minQuantity > 1 && <p className="text-slate-500 text-xs">Min. {t.minQuantity}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => setWalkInQty(t.id, (walkInItems[t.id] || 0) - 1)}
                          className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-600 text-white flex items-center justify-center transition-colors">
                          <Minus className="h-3.5 w-3.5"/>
                        </button>
                        <span className="text-white font-bold w-6 text-center tabular-nums">{walkInItems[t.id] || 0}</span>
                        <button onClick={() => setWalkInQty(t.id, (walkInItems[t.id] || 0) + 1)}
                          className="w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors">
                          <Plus className="h-3.5 w-3.5"/>
                        </button>
                      </div>
                      <p className="text-right min-w-[80px] text-blue-300 font-bold tabular-nums">
                        {(walkInItems[t.id] || 0) > 0 ? `৳${((walkInItems[t.id] || 0) * t.price).toLocaleString()}` : '—'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Sidebar summary */}
              <div>
                <Card className="bg-slate-800/50 border-slate-700 sticky top-4">
                  <CardContent className="p-5 space-y-4">
                    <p className="text-white font-semibold">Order Summary</p>
                    <div className="space-y-2 min-h-[40px]">
                      {Object.entries(walkInItems).filter(([, q]) => q > 0).map(([tid, qty]) => {
                        const t = types.find(t => t.id === parseInt(tid));
                        return t ? (
                          <div key={tid} className="flex justify-between text-sm">
                            <span className="text-slate-400">{qty}× {t.name}</span>
                            <span className="text-white">৳{(qty * t.price).toLocaleString()}</span>
                          </div>
                        ) : null;
                      })}
                      {walkInItemCount === 0 && <p className="text-slate-500 text-sm">No tickets selected</p>}
                    </div>
                    <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                      <span className="text-white font-bold">Total</span>
                      <span className="text-blue-300 font-bold text-lg tabular-nums">৳{walkInTotal.toLocaleString()}</span>
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={walkInItemCount === 0} onClick={() => setWalkInStep(2)}>
                      Continue
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 2: Customer Info */}
          {!walkInSuccess && walkInStep === 2 && (
            <Card className="max-w-md bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input value={walkInCustomer.name} onChange={e => setWalkInCustomer(p => ({ ...p, name: e.target.value }))}
                    className="bg-slate-700 border-slate-600" placeholder="Full name" />
                </div>
                <div className="space-y-2">
                  <Label>Phone <span className="text-slate-500 text-xs font-normal">(optional)</span></Label>
                  <Input value={walkInCustomer.phone} onChange={e => setWalkInCustomer(p => ({ ...p, phone: e.target.value }))}
                    className="bg-slate-700 border-slate-600" placeholder="+880..." />
                </div>
                <div className="space-y-2">
                  <Label>Visit Date</Label>
                  <div className="bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white text-sm">
                    {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} <span className="text-slate-400 text-xs ml-1">(Today)</span>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="border-slate-600" onClick={() => setWalkInStep(1)}>Back</Button>
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!walkInCustomer.name.trim() || !walkInCustomer.visitDate}
                    onClick={() => setWalkInStep(3)}>
                    Continue
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Payment */}
          {!walkInSuccess && walkInStep === 3 && (
            <div className="max-w-md space-y-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-5 space-y-3">
                  <p className="text-white font-semibold">Order Summary</p>
                  {Object.entries(walkInItems).filter(([, q]) => q > 0).map(([tid, qty]) => {
                    const t = types.find(t => t.id === parseInt(tid));
                    return t ? (
                      <div key={tid} className="flex justify-between text-sm">
                        <span className="text-slate-400">{qty}× {t.name}</span>
                        <span className="text-white">৳{(qty * t.price).toLocaleString()}</span>
                      </div>
                    ) : null;
                  })}
                  <div className="border-t border-slate-700 pt-2 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Customer</span>
                      <span className="text-slate-300">{walkInCustomer.name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Visit Date</span>
                      <span className="text-slate-300">{walkInCustomer.visitDate}</span>
                    </div>
                  </div>
                  <div className="border-t border-slate-700 pt-2 flex justify-between items-center">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-blue-300 font-bold text-lg tabular-nums">৳{walkInTotal.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-5 space-y-3">
                  <Label>Payment Method</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Cash', 'bKash', 'Bank'].map(method => (
                      <button key={method} onClick={() => setWalkInPaymentMethod(method)}
                        className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                          walkInPaymentMethod === method
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}>{method}</button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" className="border-slate-600" onClick={() => setWalkInStep(2)}>Back</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleWalkIn} disabled={walkInLoading}>
                  {walkInLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <CheckCircle2 className="h-4 w-4 mr-1.5"/>}
                  Confirm & Issue Tickets
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Ticket Type Dialog */}
      <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Ticket Type' : 'Add Ticket Type'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingType ? 'Update ticket details and inclusions.' : 'Design a new ticket type for your resort.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveType} className="space-y-4">
            {/* Live preview */}
            <TicketCard ticket={{ ...typeForm, price: typeForm.price || 0, inclusions: typeForm.inclusions.filter(Boolean) }} isActive={true} />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ticket Name</Label>
                <Input value={typeForm.name} onChange={e => setTypeForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600" placeholder="e.g. Water World Day Pass" required />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input value={typeForm.code} onChange={e => setTypeForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="bg-slate-700 border-slate-600 font-mono" placeholder="e.g. WW" maxLength={10} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={typeForm.typeId} onValueChange={v => setTypeForm(p => ({ ...p, typeId: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="0">Normal (per person)</SelectItem>
                    <SelectItem value="1">Corporate (min qty)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price per ticket (৳)</Label>
                <Input type="number" min="0" step="0.01" value={typeForm.price} onChange={e => setTypeForm(p => ({ ...p, price: e.target.value }))}
                  className="bg-slate-700 border-slate-600" required />
              </div>
            </div>

            {typeForm.typeId === '1' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum Quantity</Label>
                  <Input type="number" min="1" value={typeForm.minQuantity} onChange={e => setTypeForm(p => ({ ...p, minQuantity: e.target.value }))}
                    className="bg-slate-700 border-slate-600" />
                </div>
                <div className="space-y-2">
                  <Label>Persons per Ticket</Label>
                  <Input type="number" min="1" value={typeForm.personsPerTicket} onChange={e => setTypeForm(p => ({ ...p, personsPerTicket: e.target.value }))}
                    className="bg-slate-700 border-slate-600" placeholder="e.g. 10" />
                  <p className="text-slate-500 text-xs">1 ticket admits this many people</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map(c => (
                  <button key={c.value} type="button" onClick={() => setTypeForm(p => ({ ...p, color: c.value }))}
                    className={`w-8 h-8 rounded-lg transition-all ${typeForm.color === c.value ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800 scale-110' : ''}`}
                    style={{ background: c.value }} title={c.label} />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
              <Input value={typeForm.description} onChange={e => setTypeForm(p => ({ ...p, description: e.target.value }))}
                className="bg-slate-700 border-slate-600" placeholder="Short description of this ticket" />
            </div>

            <div className="space-y-2">
              <Label>Ticket Image <span className="text-slate-500 font-normal text-xs">(optional — shown on guest ticket card)</span></Label>
              {typeForm.imageUrl && !imageFile && (
                <div className="flex items-center gap-3 bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2">
                  <img src={typeForm.imageUrl} alt="current" className="h-10 w-10 object-cover rounded" />
                  <span className="text-slate-400 text-xs flex-1 truncate">Current image</span>
                  <button type="button" onClick={() => setTypeForm(p => ({ ...p, imageUrl: '' }))}
                    className="text-xs text-red-400 hover:text-red-300">Remove</button>
                </div>
              )}
              <input
                type="file" accept="image/*"
                onChange={e => setImageFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer cursor-pointer"
              />
              {imageFile && (
                <p className="text-xs text-green-400">Ready to upload: {imageFile.name}</p>
              )}
              {imageUploading && (
                <p className="text-xs text-blue-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading image…
                </p>
              )}
            </div>

            {/* Inclusions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>What's Included</Label>
                <button type="button" onClick={() => setTypeForm(p => ({ ...p, inclusions: [...p.inclusions, ''] }))}
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  <Plus className="h-3 w-3" />Add
                </button>
              </div>
              {typeForm.inclusions.map((inc, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={inc}
                    onChange={e => setTypeForm(p => ({ ...p, inclusions: p.inclusions.map((v, idx) => idx === i ? e.target.value : v) }))}
                    className="bg-slate-700 border-slate-600 text-sm" placeholder={`e.g. Pool Access, Lunch, Parking`} />
                  {typeForm.inclusions.length > 1 && (
                    <button type="button" onClick={() => setTypeForm(p => ({ ...p, inclusions: p.inclusions.filter((_, idx) => idx !== i) }))}
                      className="text-red-400 hover:text-red-300 flex-shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTypeDialogOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingType ? 'Update' : 'Add Ticket Type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Verify Payment Dialog */}
      {verifyTarget && (
        <Dialog open={true} onOpenChange={() => setVerifyTarget(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                Verify Payment — <span className="font-mono text-blue-400 text-sm">{verifyTarget.orderRef}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* Guest submission */}
              <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-3 space-y-2 text-sm">
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Guest Submitted</p>
                {verifyTarget.method && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Method</span>
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{verifyTarget.method}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Transaction ID</span>
                  <span className="font-mono text-green-400 text-xs bg-green-900/30 px-2 py-0.5 rounded border border-green-700/40">{verifyTarget.txnId}</span>
                </div>
                {verifyTarget.submittedAmount > 0 && (
                  <div className="flex justify-between items-center border-t border-emerald-700/30 pt-2">
                    <span className="text-slate-400">Amount Sent</span>
                    <span className="text-emerald-300 font-bold text-base">৳{Number(verifyTarget.submittedAmount).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="bg-slate-700/50 rounded-lg p-3 flex justify-between text-sm">
                <span className="text-slate-400">Order Total</span>
                <span className="text-white font-bold">৳{Number(verifyTarget.totalAmount).toLocaleString()}</span>
              </div>

              {/* Verify checkbox */}
              <label className="flex items-start gap-3 cursor-pointer select-none bg-slate-700/40 rounded-lg p-3">
                <input
                  type="checkbox"
                  checked={verifyChecked}
                  onChange={e => setVerifyChecked(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-emerald-500 flex-shrink-0"
                />
                <span className="text-sm text-slate-300 leading-relaxed">
                  I verified{verifyTarget.method ? <strong className="text-white"> {verifyTarget.method}</strong> : ''} txn{' '}
                  <strong className="font-mono text-green-400">{verifyTarget.txnId}</strong> is valid
                </span>
              </label>

              <Button
                onClick={handleVerifyConfirm}
                disabled={verifyLoading || !verifyChecked}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {verifyLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
                Confirm Payment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Decline Ticket Payment Dialog ── */}
      {declineTarget && (
        <Dialog open={true} onOpenChange={() => setDeclineTarget(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ShieldX className="h-5 w-5 text-red-400" />
                Decline Payment — <span className="font-mono text-amber-400 text-sm">{declineTarget.orderRef}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <p className="text-slate-400 text-sm">
                This will clear the guest's submitted payment reference. The guest will be able to re-submit.
              </p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">Reason for declining <span className="text-red-400">*</span></label>
                <textarea
                  rows={3}
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="e.g. Invalid transaction ID, amount mismatch..."
                  className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-500 resize-none"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => setDeclineTarget(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-700 hover:bg-red-800 text-white"
                  disabled={declineLoading || !declineReason.trim()}
                  onClick={handleDeclineConfirm}
                >
                  {declineLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldX className="h-4 w-4 mr-1.5" />}
                  Decline Payment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* ── Edit Order Dialog ── */}
      {editTarget && (
        <Dialog open={true} onOpenChange={() => setEditTarget(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-4 w-4 text-blue-400" /> Edit Order
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Ref: <span className="font-mono text-blue-400">{editTarget.orderRef}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Guest Name *</Label>
                <Input value={editForm.leadName}
                  onChange={e => setEditForm(p => ({ ...p, leadName: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white" placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Phone</Label>
                <Input value={editForm.leadPhone}
                  onChange={e => setEditForm(p => ({ ...p, leadPhone: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white" placeholder="+880..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Visit Date *</Label>
                <Input type="date" value={editForm.visitDate}
                  onChange={e => setEditForm(p => ({ ...p, visitDate: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditTarget(null)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={editLoading}
                className="bg-blue-600 hover:bg-blue-700">
                {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Edit className="h-4 w-4 mr-1.5" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TicketPage;
